// Ported verbatim (behaviour-preserving) from
// cleverstory-application-v2/src/services/report.service.jsx
import moment, { Moment } from "moment";
import buildUrl from "build-url-ts";
import { DateRangeType, ReportDateFilter, ReportDateRequest } from "src/types/experiences/leadsAndReports";
import { FOURTEEN_DAYS } from "src/pages/experiences/leadsAndReports/constants";

export const getReportDatesForOption = (
  key: DateRangeType,
): { startDate: Moment; endDate: Moment } => {
  switch (key) {
    case "today":
      return { startDate: moment(), endDate: moment() };
    case "yesterday":
      return { startDate: moment().subtract(1, "days"), endDate: moment().subtract(1, "days") };
    case "7Days":
      return { startDate: moment().subtract(7, "days"), endDate: moment() };
    case "14Days":
      return { startDate: moment().subtract(14, "days"), endDate: moment() };
    case "WTD":
      return { startDate: moment().startOf("week"), endDate: moment() };
    case "MTD":
      return { startDate: moment().startOf("month"), endDate: moment() };
    case "30Days":
      return { startDate: moment().subtract(30, "days"), endDate: moment() };
    case "90Days":
      return { startDate: moment().subtract(90, "days"), endDate: moment() };
    default:
      return { startDate: moment(), endDate: moment() };
  }
};

export const defaultDateFilter = (): ReportDateFilter => ({
  type: FOURTEEN_DAYS,
  ...getReportDatesForOption(FOURTEEN_DAYS),
});

const queryParamsFromDate = (value: ReportDateFilter) => ({
  type: value.type,
  startDate: value.startDate.format("DD/MM/YYYY"),
  endDate: value.endDate.format("DD/MM/YYYY"),
});

export const generateReportUrl = (
  url: string,
  reportDate: ReportDateFilter,
  paramsString: string,
): string => {
  const params = new URLSearchParams(paramsString);
  const query = { ...Object.fromEntries(params), ...queryParamsFromDate(reportDate) };
  return buildUrl(url, { queryParams: query }) ?? url;
};

export const parseQueryParams = (search: string): ReportDateFilter => {
  const params = new URLSearchParams(search);
  const type = params.get("type") as DateRangeType | null;
  const startDate = params.get("startDate");
  const endDate = params.get("endDate");

  if (!type) {
    return defaultDateFilter();
  }

  return {
    type,
    startDate: moment(startDate, "DD/MM/YYYY"),
    endDate: moment(endDate, "DD/MM/YYYY"),
  };
};

/**
 * Mirrors legacy convertDatesToTimezone: endDate is bumped +1 day and both are
 * formatted to UTC ISO. Used as the request fragment AND the cache `dateKey`.
 */
export const convertDatesToTimezone = (value?: ReportDateFilter): ReportDateRequest => {
  if (!value) return {};
  const startDate = value.startDate;
  let endDate = value.endDate;
  endDate = moment(endDate).add(1, "days");

  if (!startDate || !endDate) return {};

  return {
    startDate: startDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss+00:00"),
    endDate: endDate.startOf("day").format("YYYY-MM-DDTHH:mm:ss+00:00"),
  };
};

/** Stable serialisation of the date filter for react-query cache keys. */
export const dateKey = (value?: ReportDateFilter): string => {
  const { startDate, endDate } = convertDatesToTimezone(value);
  return `${value?.type ?? "none"}|${startDate ?? ""}|${endDate ?? ""}`;
};
